import { useEffect } from 'react';
import i18n from 'i18n-js';
import { useSelector } from 'react-redux';

import { Alert } from '@helpers';
import { useAppDispatch } from '@store/hooks/useAppDispatch';
import { clearCloseError, selectLastCloseError } from '@reducers/v2/planboard/planboard.slice';
import { CLOSE_PRODUCTION_OPERATION_ERROR_CODES } from '@v2/components/FinishJobModal/constants';

interface UseCloseProductionOperationErrorAlertParams {
  // Called when backend rejects with errorCode 'JobAlreadyFinished'. The job IS finished
  // in ERP — consumers should sync local state to mark the operation as Finished, and the
  // alert is suppressed since the user's intent was effectively achieved.
  onAlreadyFinished?: (productionOperationId: number) => void;
}

export const useCloseProductionOperationErrorAlert = ({ onAlreadyFinished }: UseCloseProductionOperationErrorAlertParams = {}) => {
  const dispatch = useAppDispatch();
  const lastCloseError = useSelector(selectLastCloseError);

  useEffect(() => {
    if (!lastCloseError) return;

    const { productionOperationId, errorMessage, validationErrors } = lastCloseError;

    if (validationErrors?.some((e) => e.errorCode === CLOSE_PRODUCTION_OPERATION_ERROR_CODES.JOB_ALREADY_FINISHED)) {
      onAlreadyFinished?.(productionOperationId);
    } else {
      // Title shows our framing; text carries BE's actual message so the user knows what failed.
      // Render as a dialog (with OK) rather than a toast — toasts auto-dismiss in 4s and are easy to miss on busy screens.
      const detail = validationErrors?.length
        ? validationErrors.map((e) => e.errorMessage).filter(Boolean).join('\n')
        : errorMessage;
      Alert({
        title: i18n.t('Failed to mark job as finished'),
        text: detail || undefined,
        actions: [{ text: i18n.t('OK'), style: 'primary' }],
      });
    }

    dispatch(clearCloseError());
  }, [lastCloseError, dispatch, onAlreadyFinished]);
};
